import * as React from "react";
import { cn } from "lib/utils";
import { tone, toneForStatus, humanizeStatus, type Tone } from "components/ui/tone";

const SIZE = {
    xs: "px-1.5 py-0.5 text-2xs gap-1 [&>svg]:w-2.5 [&>svg]:h-2.5",
    sm: "px-2 py-0.5 text-micro gap-1 [&>svg]:w-3 [&>svg]:h-3",
    md: "px-2.5 py-1 text-meta gap-1.5 [&>svg]:w-3.5 [&>svg]:h-3.5",
    lg: "px-3 py-1.5 text-ui gap-1.5 [&>svg]:w-4 [&>svg]:h-4",
} as const;

export interface StatusBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
    /** Raw status string from the API — mapped to a tone automatically */
    status?: string | null;
    /** Override the automatic tone mapping */
    tone?: Tone;
    /** Override the humanised label */
    label?: React.ReactNode;
    size?: keyof typeof SIZE;
    /** Leading status dot. On by default — colour alone must not carry meaning */
    dot?: boolean;
    /** Optional leading icon (replaces the dot) */
    icon?: React.ReactNode;
    /** Longer description surfaced to assistive tech and as a tooltip */
    description?: string;
}

/**
 * Canonical status pill. One implementation now backs campaign statuses,
 * support tickets, invoices, identities and subscriptions — all of which
 * previously had their own badge with no dark-mode variants.
 */
export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
    (
        {
            status,
            tone: toneOverride,
            label,
            size = "sm",
            dot = true,
            icon,
            description,
            className,
            ...props
        },
        ref
    ) => {
        const resolved = toneOverride ?? toneForStatus(status);
        const tk = tone(resolved);
        const text = label ?? humanizeStatus(status);

        return (
            <span
                ref={ref}
                role="status"
                title={description}
                aria-label={description ?? (typeof text === "string" ? `Status: ${text}` : undefined)}
                className={cn(
                    "inline-flex items-center rounded-full font-semibold w-fit whitespace-nowrap align-middle",
                    SIZE[size],
                    tk.badge,
                    className
                )}
                {...props}
            >
                {icon ?? (dot && (
                    <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", tk.solid)} aria-hidden="true" />
                ))}
                <span className="truncate">{text}</span>
            </span>
        );
    }
);
StatusBadge.displayName = "StatusBadge";

export default StatusBadge;
